import { useMemo, useState, type ReactNode } from 'react'
import type { CostTermsGroup, CostWeek } from '../../../lib/api'
import { Loader2 } from 'lucide-react'
import { ChartWrapper } from '../ChartWrapper'
import { useCostWeeks } from './useCostWeeks'
import { termsColours } from './termsPalette'
import { useSeriesToggle } from '../charts/useSeriesToggle'
import { WeekPager } from './WeekPager'
import { IncomingPerWeekChart, incomingLegend } from './IncomingPerWeekChart'
import { PaymentsDueChart } from './PaymentsDueChart'
import CostWeekLinesModal, { type CostWeekLinesTarget } from './CostWeekLinesModal'

/** CSV rows: one column per terms group rather than a nested object. */
function csvRows<W extends CostWeek>(weeks: W[], groups: CostTermsGroup[]) {
  return weeks.map(({ terms, ...rest }) => {
    const row: Record<string, unknown> = { ...rest }
    for (const g of groups) row[g.label] = terms[g.key]?.value ?? 0
    return row
  })
}

interface CostWeeklyChartsProps {
  /** The dashboard's Cost request params; the charts are scoped by them. */
  params?: Record<string, string | number | boolean>
  /** The existing weekly chart, still shown to schedule-line scopes. */
  fallback: ReactNode
}

/**
 * CR-1421: the Cost › Future weekly charts. Both look forward from this week,
 * twelve weeks a page, and page together. Schedule-line scopes keep the
 * existing chart (`fallback`): schedule lines are left alone.
 */
export function CostWeeklyCharts({ params, fallback }: CostWeeklyChartsProps) {
  // No Cost payload yet (or a caller without one): nothing to scope by, so no fetch.
  if (!params) return <>{fallback}</>
  return <WeeklyCharts params={params} fallback={fallback} />
}

function WeeklyCharts({ params, fallback }: { params: Record<string, string | number | boolean>; fallback: ReactNode }) {
  const { data, loading, error, page, setPage } = useCostWeeks(params)
  const goodsSeries = useSeriesToggle()
  const paySeries = useSeriesToggle()
  const [target, setTarget] = useState<CostWeekLinesTarget | null>(null)
  const weeks = data && data.grain === 'po_lines' ? data : null
  const colours = useMemo(() => termsColours(weeks?.terms_groups ?? []), [weeks])

  if (data && !weeks) return <>{fallback}</>
  if (!weeks) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border bg-white text-sm text-muted-foreground">
        {error ? 'The weekly charts could not be loaded. Refresh the dashboard to try again.' : <Loader2 className="h-4 w-4 animate-spin" />}
      </div>
    )
  }
  const first = weeks.incoming[0]
  const last = weeks.incoming[weeks.incoming.length - 1]
  const pager = (
    <WeekPager page={page} from={first?.week_start ?? undefined} to={last?.week_end} loading={loading}
      hasNext={weeks.has_next} onPageChange={setPage} />
  )
  const legend = incomingLegend(weeks.terms_groups, colours)

  return (
    <>
      <ChartWrapper
        stackHeader
        title="Incoming Per Week by Latest Supplier Promise Date"
        subtitle="The selected period, week by week. Arrears (open lines past their promise date) show on this week only, when the period includes it. The value bar is split by the supplier code's payment terms."
        data={csvRows(weeks.incoming, weeks.terms_groups)}
        exportFilename="incoming-by-week"
        legend={legend}
        visibleSeries={goodsSeries.visibleSeries}
        onSeriesToggle={goodsSeries.onSeriesToggle}
        headerActions={pager}
      >
        <IncomingPerWeekChart
          weeks={weeks.incoming} groups={weeks.terms_groups} colours={colours} mean={weeks.incoming_mean}
          isHidden={goodsSeries.isHidden}
          onPick={({ week, terms, subset }) => setTarget({ chart: 'incoming', bar: week, terms, subset })}
        />
      </ChartWrapper>
      <ChartWrapper
        stackHeader
        title="Payments Due per Week by Expected Payment Date"
        subtitle="Open line value by the week payment falls due: promise date plus the supplier code's payment terms. Lines with no terms fall back to the promise date."
        data={csvRows(weeks.payments, weeks.terms_groups)}
        exportFilename="payments-due-by-week"
        legend={weeks.terms_groups.map((g) => ({ color: colours[g.key], label: g.label }))}
        visibleSeries={paySeries.visibleSeries}
        onSeriesToggle={paySeries.onSeriesToggle}
        headerActions={pager}
      >
        <PaymentsDueChart
          weeks={weeks.payments} groups={weeks.terms_groups} colours={colours}
          isHidden={paySeries.isHidden}
          onPick={(bar, terms) => setTarget({ chart: 'payments', bar, terms })}
        />
      </ChartWrapper>
      <CostWeekLinesModal target={target} groups={weeks.terms_groups} params={params} onClose={() => setTarget(null)} />
    </>
  )
}
